import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { FaTrash } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { bookAPI, workOrderAPI } from '../../utils/api';

function WorkOrderForm({ initial, publishers, packages, books, projects: projProp = [], onSave, onCancel }) {
  const nf = new Intl.NumberFormat('en-US');
  const projList = Array.isArray(projProp) ? projProp : [];
  const [form, setForm] = useState({ 
    orderNumber: '', 
    orderDate: '', 
    publisherId: '', 
    packageId: '', 
    projectId: '', 
    bookIds: [], 
    total_quantity: 0, 
    total_price: 0, 
    notes: '', 
    status: 'DRAFT' 
  });
  const [priceMap, setPriceMap] = useState({});
  const [qtyMap, setQtyMap] = useState({});

  const calcTotals = useCallback(() => {
    let tq = 0, tp = 0;
    form.bookIds.forEach(id => {
      const q = Number(qtyMap[id] || 0);
      const p = Number(priceMap[id] || 0);
      tq += q;
      tp += q * p;
    });
    setForm(f => ({ ...f, total_quantity: tq, total_price: tp }));
  }, [form.bookIds, qtyMap, priceMap]);

  useEffect(() => { calcTotals(); }, [calcTotals]);

  useEffect(() => {
    if (initial) {
      const pm = {}, qm = {}, ids = [];
      if (initial.items) {
        initial.items.forEach(it => {
          ids.push(it.book_id || it.bookId);
          pm[it.book_id || it.bookId] = it.unit_price || it.unitPrice;
          qm[it.book_id || it.bookId] = it.quantity;
        });
      }
      setForm({
        orderNumber: initial.order_number || initial.orderNumber || '',
        orderDate: initial.order_date || initial.orderDate || '',
        publisherId: (initial.publisher?.id || initial.vendor?.id)?.toString() || '',
        packageId: initial.package?.id?.toString() || '',
        projectId: initial.project?.id?.toString() || '',
        bookIds: ids,
        total_quantity: initial.total_quantity || initial.totalQuantity || 0,
        total_price: initial.total_price || initial.totalPrice || 0,
        status: initial.status || 'DRAFT',
        notes: initial.notes || ''
      });
      setPriceMap(pm);
      setQtyMap(qm);
    }
  }, [initial]);

  const change = e => { const { name, value } = e.target; setForm(f => ({ ...f, [name]: value })); };

  const validateOrderNumber = async (e) => {
    const val = e.target.value.trim();
    if (!val || (initial && (initial.order_number === val || initial.orderNumber === val))) return;
    try {
      const r = await workOrderAPI.exists(val);
      if (r.data.exists) {
        await Swal.fire({ icon: 'error', title: 'Order number exists' });
        setForm(f => ({ ...f, orderNumber: '' }));
      }
    } catch (err) { /* ignore */ }
  };

  const submit = e => {
    e.preventDefault();
    const items = form.bookIds.map(id => ({
      bookId: id,
      unitPrice: priceMap[id] ?? 0,
      quantity: parseInt(qtyMap[id] || 1)
    }));
    const payload = {
      orderNumber: form.orderNumber,
      orderDate: form.orderDate || null,
      publisherId: form.publisherId ? Number(form.publisherId) : null,
      packageId: form.packageId ? Number(form.packageId) : null,
      projectId: form.projectId ? Number(form.projectId) : null,
      status: form.status || null,
      notes: form.notes || null,
      items
    };
    onSave(payload);
  };

  const bookOptions = books.map(b => ({ value: b.id, label: `${b.book_code || b.bookCode || ''} - ${b.name}` }));
  
  const onBookChange = opts => {
    const ids = opts ? opts.map(o => o.value) : [];
    setForm(f => ({ ...f, bookIds: ids }));
    setQtyMap(m => { const n = { ...m }; ids.forEach(id => { if (!n[id]) n[id] = 1; }); return n; });
    setPriceMap(m => {
      const n = { ...m };
      ids.forEach(id => {
        if (!n[id]) {
          const bk = books.find(b => b.id === id);
          n[id] = bk?.price || 0;
        }
      });
      return n;
    });
  };

  const selectedBooks = books.filter(b => form.bookIds.includes(b.id));
  
  const removeBook = id => {
    setForm(f => ({ ...f, bookIds: f.bookIds.filter(bid => bid !== id) }));
    setQtyMap(m => { const n = { ...m }; delete n[id]; return n; });
    setPriceMap(m => { const n = { ...m }; delete n[id]; return n; });
  };

  const changePrice = (id, valRaw) => {
    const val = Number(valRaw);
    Swal.fire({
      title: 'Update book price?',
      text: 'Do you want to update the default price for this book in the inventory?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#2ecc71'
    }).then(async res => {
      if (res.isConfirmed) {
        try {
          await bookAPI.update(id, { price: val });
          setPriceMap(m => ({ ...m, [id]: val }));
          Swal.fire({ icon: 'success', title: 'Updated', text: 'Price saved to inventory', toast: true, position: 'top-end', timer: 3000 });
        } catch (e) {
          Swal.fire({ icon: 'error', title: 'Error', text: e.message });
        }
      } else {
        setPriceMap(m => ({ ...m, [id]: val }));
      }
    });
  };

  const changeQty = (id, val) => { setQtyMap(m => ({ ...m, [id]: Number(val) })); };

  return (
    <form onSubmit={submit} className="eduss-student-create-form workorder-form-grid">
      <div className="eduss-student-create-field">
        <label>Project</label>
        <select name="projectId" value={form.projectId} onChange={change}>
          <option value="">Select Project</option>
          {projList.map(p => (<option key={p.id} value={p.id.toString()}>{p.name}</option>))}
        </select>
      </div>
      <div className="eduss-student-create-field">
        <label>Publisher *</label>
        <select name="publisherId" value={form.publisherId} onChange={change} required>
          <option value="">Select publisher</option>
          {publishers.map(v => (<option key={v.id} value={v.id.toString()}>{v.name}</option>))}
        </select>
      </div>
      <div className="eduss-student-create-field">
        <label>Order Number *</label>
        <input name="orderNumber" value={form.orderNumber} onChange={change} onBlur={validateOrderNumber} required />
      </div>
      <div className="eduss-student-create-field">
        <label>Order Date</label>
        <input type="date" name="orderDate" value={form.orderDate} onChange={change} />
      </div>
      <div className="eduss-student-create-field">
        <label>Package *</label>
        <select name="packageId" value={form.packageId} onChange={change} required>
          <option value="">Select Package</option>
          {packages.map(p => (<option key={p.id} value={p.id.toString()}>{p.name}</option>))}
        </select>
      </div>
      <div className="eduss-student-create-field">
        <label>Books</label>
        <Select 
          isMulti 
          options={bookOptions} 
          value={bookOptions.filter(o => form.bookIds.includes(o.value))} 
          onChange={onBookChange}
          placeholder="Search and add books..."
        />
      </div>
      <div className="eduss-student-create-field">
        <label>Status</label>
        <select name="status" value={form.status} onChange={change}>
          <option>DRAFT</option>
          <option>PENDING</option>
          <option>APPROVED</option>
          <option>COMPLETED</option>
          <option>CANCELLED</option>
        </select>
      </div>
      <div className="eduss-student-create-field" style={{ flexBasis: '100%' }}>
        <label>Notes</label>
        <input name="notes" value={form.notes || ''} onChange={change} placeholder="Internal order notes..." />
      </div>
      
      <div className="eduss-student-create-field">
        <label>Total Quantity</label>
        <input value={nf.format(form.total_quantity || 0)} readOnly style={{ background: '#f9f9f9' }} />
      </div>
      <div className="eduss-student-create-field">
        <label>Total Price</label>
        <input value={`Rs. ${nf.format(form.total_price || 0)}`} readOnly style={{ background: '#f9f9f9', fontWeight: 'bold', color: '#0f6fad' }} />
      </div>

      {selectedBooks.length > 0 && (
        <div className="grid-span-2" style={{ maxHeight: '300px', overflowY: 'auto', marginTop: 20, borderRadius: 12, border: '1px solid #eee' }}>
          <table className="eduss-global-datatable mini">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Price</th>
                <th>Qty</th>
                <th>Subtotal</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {selectedBooks.map(b => (
                <tr key={b.id}>
                  <td>{b.book_code || b.bookCode || '-'}</td>
                  <td style={{ fontWeight: 600 }}>{b.name}</td>
                  <td>
                    <input 
                      type="number" 
                      step="0.01" 
                      value={priceMap[b.id] ?? 0} 
                      onChange={e => setPriceMap(m => ({ ...m, [b.id]: Number(e.target.value) }))} 
                      onBlur={e => changePrice(b.id, e.target.value)} 
                      style={{ width: 80, padding: '4px 8px', borderRadius: 6, border: '1px solid #ddd' }}
                    />
                  </td>
                  <td>
                    <input 
                      type="number" 
                      value={qtyMap[b.id] || 1} 
                      onChange={e => changeQty(b.id, e.target.value)} 
                      style={{ width: 70, padding: '4px 8px', borderRadius: 6, border: '1px solid #ddd' }}
                    />
                  </td>
                  <td>Rs. {nf.format((priceMap[b.id] || 0) * (qtyMap[b.id] || 0))}</td>
                  <td>
                    <button type="button" onClick={() => removeBook(b.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e74c3c' }}>
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="eduss-student-create-actions" style={{ marginTop: 30 }}>
        <button type="button" className="eduss-student-create-btn cancel" onClick={onCancel}>Cancel</button>
        <button type="submit" className="eduss-student-create-btn submit">Save Work Order</button>
      </div>
    </form>
  );
}

WorkOrderForm.propTypes = {
  initial: PropTypes.object,
  publishers: PropTypes.array,
  packages: PropTypes.array,
  books: PropTypes.array,
  projects: PropTypes.array,
  onSave: PropTypes.func,
  onCancel: PropTypes.func
};

export default WorkOrderForm;
